import React, { useCallback, useContext, useMemo, useState } from 'react';
import ChatContext from '../../services/ChatContext';
import { useDebounce } from '../../services/utils';
import ConversationCardV2 from './ConversationCardV2';
import { SearchIcon } from './icons';
import {
  Rail,
  RailHeader,
  RailTitle,
  RailBadge,
  SearchPill,
  SearchInput,
  RailList,
  SkeletonRow,
  Skeleton,
  HintBar,
} from './styles';

const RailSkeleton = () =>
  Array.from({ length: 7 }).map((_, i) => (
    <SkeletonRow key={i}>
      <Skeleton w="4.8rem" h="4.8rem" style={{ borderRadius: '50%' }} />
      <div style={{ flex: 1 }}>
        <Skeleton w="60%" h="1.5rem" />
        <div style={{ height: 8 }} />
        <Skeleton w="85%" h="1.3rem" />
      </div>
    </SkeletonRow>
  ));

const ConversationRailV2 = ({ activeId, onSelect }) => {
  const {
    conversations,
    loadingConversations,
    fetchMoreConversations,
    hasMoreConversations,
    totalUnreadCount,
  } = useContext(ChatContext);

  const [term, setTerm] = useState('');
  const delayed = useDebounce(term, 400);

  const list = useMemo(() => {
    const lowered = (delayed || '').toLowerCase();
    const lastActivity = (c) => c.lastMessage?.sentAt || 0;
    return Object.values(conversations || {})
      .filter((c) => (lowered ? c.user?.name?.toLowerCase().includes(lowered) : true))
      .sort((a, b) => {
        const diff = lastActivity(b) - lastActivity(a);
        if (diff !== 0) return diff;
        const unread = (b.unreadMessageCount || 0) - (a.unreadMessageCount || 0);
        if (unread !== 0) return unread;
        return (a.user?.name || '').localeCompare(b.user?.name || '');
      });
  }, [conversations, delayed]);

  const handleScroll = useCallback(
    (e) => {
      if (delayed || !hasMoreConversations) return;
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      if (scrollHeight - scrollTop - clientHeight < 240) fetchMoreConversations();
    },
    [delayed, hasMoreConversations, fetchMoreConversations],
  );

  return (
    <Rail hideOnMobile={!!activeId}>
      <RailHeader>
        <RailTitle>Messages</RailTitle>
        {totalUnreadCount > 0 && <RailBadge>{totalUnreadCount} unread</RailBadge>}
      </RailHeader>
      <SearchPill>
        <SearchIcon size={18} style={{ opacity: 0.5, flexShrink: 0 }} />
        <SearchInput
          placeholder="Search conversations"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
        />
      </SearchPill>
      <RailList onScroll={handleScroll}>
        {loadingConversations && !list.length ? (
          <RailSkeleton />
        ) : (
          list.map((c) => (
            <ConversationCardV2
              key={c.groupId}
              conversation={c}
              active={activeId === c.groupId}
              onClick={() => onSelect(c)}
            />
          ))
        )}
        {!loadingConversations && !list.length && (
          <HintBar>{delayed ? 'No matches' : 'No conversations yet'}</HintBar>
        )}
        {hasMoreConversations && !delayed && list.length > 0 && (
          <HintBar>Scroll for more…</HintBar>
        )}
      </RailList>
    </Rail>
  );
};

export default ConversationRailV2;
